<template>
  <!-- Native: PrimeVue always keeps label=&nbsp; which shifts icon left in flex. -->
  <button
    v-if="isIconOnly"
    type="button"
    class="toolbar-btn toolbar-btn--icon-only"
    :title="toolbarTooltip"
    :aria-label="toolbarTooltip || undefined"
    @click="handleClick"
  >
    <span v-if="isQuestionMarkIcon" class="toolbar-btn__qmark" aria-hidden="true">?</span>
    <img
      v-else-if="iconResolution.kind === 'image'"
      :src="iconResolution.url"
      alt=""
      class="toolbar-btn__icon-img"
    />
    <i v-else-if="iconResolution.kind === 'prime'" :class="iconResolution.primeClass" />
  </button>

  <Button
    v-else
    :class="['toolbar-btn', { 'has-children': hasChildItems }]"
    :label="hasChildItems ? undefined : toolbarLabel"
    :icon="hasChildItems ? undefined : primeIconClass"
    :title="toolbarTooltip"
    @click="handleClick"
  >
    <template v-if="hasChildItems">
      <i :class="getItemIcon(item)" />
      <i class="pi pi-chevron-down dropdown-icon" />
    </template>
    <template v-if="!hasChildItems && iconResolution.kind === 'image'" #icon>
      <img
        :src="iconResolution.url"
        alt=""
        class="toolbar-btn__icon-img"
      />
    </template>
  </Button>

  <Menu
    v-if="hasChildItems"
    ref="menuRef"
    :model="childMenuItems"
    :popup="true"
  />
</template>

<script setup lang="ts">
import { computed, inject, ref, type ComputedRef } from 'vue'
import { useRouter } from 'vue-router'
import Button from 'primevue/button'
import Menu from 'primevue/menu'
import En from '@/common/classes/En'
import { useMenu } from '@/common/composables/useMenu'
import { EnKey } from '@/components/platform/injection-keys'
import { useAppStore } from '@/stores/app'
import { useDocumentTabsStore } from '@/stores/documentTabs'
import { navigateMenuItem } from '@/services/menu/menuItemNavigation'
import type { MenuItem as PrimeMenuItem } from 'primevue/menuitem'
import type { MenuItem } from '@/services/parse/types'

interface Props {
  item: MenuItem
}

const props = defineProps<Props>()

const router = useRouter()
const appStore = useAppStore()
const documentTabsStore = useDocumentTabsStore()
const en = inject<ComputedRef<En | null>>(EnKey, null)
const {
  getItemIcon,
  getItemCounter,
  hasChildren,
  formatItemName,
  formatToolbarLabel,
  resolveMenuItemIcon,
} = useMenu()
const menuRef = ref<InstanceType<typeof Menu> | null>(null)

const hasChildItems = computed(() => hasChildren(props.item))

const hideTitle = computed(() => !!props.item?.hideTitle)

const iconResolution = computed(() => resolveMenuItemIcon(props.item))

const iconName = computed(() =>
  String(props.item?.icon ?? '')
    .trim()
    .replace(/\.(png|gif|jpg|jpeg|svg)$/i, '')
    .toLowerCase(),
)

/** Swing help toolbar uses a plain "?" glyph. */
const isQuestionMarkIcon = computed(() => iconName.value === 'question' || iconName.value === '?')

const hasIcon = computed(() => iconResolution.value.kind !== 'none' || isQuestionMarkIcon.value)

const toolbarLabel = computed(() => formatToolbarLabel(props.item))

const toolbarTooltip = computed(() => formatItemName(props.item))

/**
 * Icon-only toolbar control — never use PrimeVue Button (it injects &nbsp; label).
 * DevTools: .p-button-icon-only + .p-button-label=&nbsp; → icon shifts left.
 */
const isIconOnly = computed(
  () =>
    !hasChildItems.value &&
    hasIcon.value &&
    (hideTitle.value || !toolbarLabel.value.trim()),
)

const primeIconClass = computed(() =>
  !hasChildItems.value && iconResolution.value.kind === 'prime'
    ? iconResolution.value.primeClass
    : undefined,
)

const childMenuItems = computed<PrimeMenuItem[]>(() => {
  if (!props.item.children) return []

  return props.item.children.map(child => ({
    label: formatItemName(child),
    icon: getItemIcon(child),
    badge: getItemCounter(child)?.toString(),
    command: () => navigateTo(child)
  }))
})

const navigateTo = async (item: MenuItem) => {
  await navigateMenuItem(item, {
    router,
    appStore,
    documentTabsStore,
    en: en?.value ?? null,
  })
}

const handleClick = (event: Event) => {
  if (hasChildItems.value && menuRef.value) {
    menuRef.value.toggle(event)
  } else {
    navigateTo(props.item)
  }
}
</script>

<style scoped lang="scss">
.toolbar-btn {
  color: rgba(255, 255, 255, 0.9) !important;
  background: rgba(255, 255, 255, 0.12) !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 8px 12px !important;
  height: 38px;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s ease;
  gap: 6px;
  white-space: nowrap;
  cursor: pointer;
  box-sizing: border-box;

  &:hover {
    background: rgba(255, 255, 255, 0.22) !important;
    transform: translateY(-1px);
  }

  &:focus {
    box-shadow: none !important;
    outline: none;
  }

  &.has-children {
    padding-right: 8px !important;
  }

  &--icon-only {
    width: 38px !important;
    min-width: 38px !important;
    height: 38px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 0 !important;
    line-height: 1 !important;
    transform: none !important;

    &:hover {
      transform: none !important;
    }

    .pi {
      display: block;
      width: 1rem;
      height: 1rem;
      font-size: 1rem;
      line-height: 1rem;
      text-align: center;
    }
  }

  &__qmark {
    display: block;
    font-size: 1.35rem;
    font-weight: 700;
    line-height: 1;
    color: inherit;
  }

  .dropdown-icon {
    font-size: 0.7rem;
    margin-left: 2px;
    opacity: 0.7;
  }

  &__icon-img {
    width: 1rem;
    height: 1rem;
    object-fit: contain;
    display: block;
  }
}

/* Fallback if an older Prime icon-only toolbar button is still mounted */
.toolbar-btn.p-button-icon-only {
  width: 38px !important;
  min-width: 38px !important;
  padding: 0 !important;
  gap: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;

  :deep(.p-button-label) {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    overflow: hidden !important;
  }

  :deep(.p-button-icon) {
    margin: 0 !important;
    margin-inline: 0 !important;
  }
}

@media (max-width: 1200px) {
  .toolbar-btn:not(.toolbar-btn--icon-only):not(.p-button-icon-only) {
    :deep(.p-button-label) {
      display: none;
    }
    padding: 8px !important;
  }
}
</style>
